import {
  Check,
  Download,
  Circle,
  ChevronDown,
  ChevronRight,
  Blend,
  Brush,
  Diff,
  Frame,
  FilePlus2,
  FileText,
  GripVertical,
  Info,
  LassoSelect,
  Menu,
  Minus,
  MousePointer2,
  Hexagon,
  Pencil,
  PaintBucket,
  PocketKnife,
  PenTool,
  Pipette,
  Redo2,
  Save,
  Scissors,
  ScissorsLineDashed,
  Shapes,
  Square,
  SquaresIntersect,
  SquaresUnite,
  Split,
  SquareArrowOutUpRight,
  Star,
  Type,
  Ungroup,
  Undo2,
  Upload,
  Workflow,
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Button } from "@/components/ui/button";
import type { BooleanOperation } from "@/document/model/nodes";
import type { PrimitiveKind } from "@/document/model/primitives";
import { documentStore, selectionStore, uiStore } from "@/editor/store";
import { AboutDialog } from "@/editor/shell/AboutDialog";
import { CURVA_OPEN_ABOUT_EVENT } from "@/editor/shell/aboutEvents";
import { SELECTION_COLOR_SAMPLE_SOURCE_ID } from "@/editor/shell/selectionColorSample";
import type { TopBarActions } from "@/editor/shell/topBarActions";
import { documentDisplayName } from "@/editor/shell/shellTransfer";
import { isNativeShell } from "@/io/platform";
import { mcpBridgeStore } from "@/mcp/mcpBridgeStore";
import { toggleMcpHostConnect } from "@/mcp/mcpBridgeClient";
import { useStoreSubscribe } from "@/lib/store";
import { NumericScrubLabel } from "@/editor/shell/right-panel/NumericScrubLabel";
import { ScrubInput } from "@/editor/shell/right-panel/ScrubInput";

export type ActiveTool = "select" | "frame" | "shape" | "path" | "pencil" | "lasso" | "text" | "fill" | "knife" | "shapeBuilder" | "sculpt" | "subdivide" | "extrude";

interface TopBarProps {
  actions: TopBarActions;
}

function useToolPanelState() {
  const tool = useStoreSubscribe(selectionStore.tool);
  const selection = useStoreSubscribe(selectionStore.selection);
  const colorSampler = useStoreSubscribe(uiStore.colorSampler);
  const sculptBrush = useStoreSubscribe(uiStore.sculptBrush);
  const subdivideSession = useStoreSubscribe(uiStore.subdivideSession);

  return {
    activeTool: tool.activeTool,
    selectedShapeKind: tool.selectedShapeKind,
    mode: selection.mode,
    selectedVertexCount: Math.max(selection.selectedVertexIds?.length ?? 0, selection.selectedVertexId ? 1 : 0),
    selectedEdgeCount: selection.selectedEdgeIds?.length ?? 0,
    hasSelectedHandle: Boolean(selection.selectedHandle),
    hasSelection: selection.selectedNodeIds.length > 0,
    selectionColorSampleActive: colorSampler?.sourceId === SELECTION_COLOR_SAMPLE_SOURCE_ID,
    sculptBrush,
    subdivideSession,
  };
}

function tooltipWithShortcut(label: string, shortcut?: string): string {
  return shortcut ? `${label} (${shortcut})` : label;
}

const tools = [
  { id: "select" as const, label: "Move", shortcut: "V", icon: MousePointer2 },
  { id: "lasso" as const, label: "Lasso", shortcut: "Q", icon: LassoSelect },
  { id: "frame" as const, label: "Frame", shortcut: "F", icon: Frame },
  { id: "text" as const, label: "Text", shortcut: "T", icon: Type },
  { id: "path" as const, label: "Path", shortcut: "P", icon: PenTool },
  { id: "pencil" as const, label: "Pencil", shortcut: "Shift+P", icon: Pencil },
];

const shapeTools: Array<{ kind: PrimitiveKind; label: string; shortcut?: string; icon: typeof Square }> = [
  { kind: "rectangle", label: "Rectangle", shortcut: "R", icon: Square },
  { kind: "oval", label: "Oval", shortcut: "O", icon: Circle },
  { kind: "line", label: "Line", shortcut: "L", icon: Minus },
  { kind: "polygon", label: "Polygon", icon: Hexagon },
  { kind: "star", label: "Star", icon: Star },
];

const booleanTools: Array<{ operation: BooleanOperation; label: string; shortcut: string; icon: typeof Square }> = [
  { operation: "union", label: "Union", shortcut: "Cmd+Alt+U", icon: SquaresUnite },
  { operation: "subtract", label: "Subtract", shortcut: "Cmd+Alt+S", icon: Diff },
  { operation: "intersect", label: "Intersect", shortcut: "Cmd+Alt+I", icon: SquaresIntersect },
  { operation: "exclude", label: "Exclude", shortcut: "Cmd+Alt+X", icon: Blend },
];

interface PanelPosition {
  x: number;
  y: number;
}

const floatingToolPanelPositionStorageKey = "curva:floating-tool-panel-position";
const floatingToolPanelViewportMargin = 8;

export function TopBar({ actions }: TopBarProps) {
  const curvaDocument = useStoreSubscribe(documentStore.document);
  const canvasDebug = useStoreSubscribe(uiStore.canvasDebug);
  const canvasSnap = useStoreSubscribe(uiStore.canvasSnap);
  const animation = useStoreSubscribe(uiStore.animation);
  const allowScripts = useStoreSubscribe(uiStore.allowScripts);
  const scriptsDebugWindowOpen = useStoreSubscribe(uiStore.scriptsDebugWindowOpen);
  const mcpBridge = useStoreSubscribe(mcpBridgeStore.state);
  const toolPanel = useToolPanelState();
  const documentName = documentDisplayName(curvaDocument.name);
  const canUndo = actions.canUndo();
  const canRedo = actions.canRedo();
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<"view" | "development" | "scripts" | undefined>(undefined);
  const [submenuAnchor, setSubmenuAnchor] = useState<DOMRect | undefined>(undefined);
  const [submenuPosition, setSubmenuPosition] = useState({ left: 0, top: 0 });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const submenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) {
      setActiveSubmenu(undefined);
      setSubmenuAnchor(undefined);
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Node ? event.target : undefined;
      if (target && (menuRef.current?.contains(target) || submenuRef.current?.contains(target))) {
        return;
      }

      setMenuOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (activeSubmenu) {
          setActiveSubmenu(undefined);
          setSubmenuAnchor(undefined);
          return;
        }
        setMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen, activeSubmenu]);

  useLayoutEffect(() => {
    if (!activeSubmenu || !submenuAnchor) {
      return;
    }

    function updateSubmenuPosition(): void {
      const submenu = submenuRef.current;
      const width = submenu?.offsetWidth || 224;
      const height = submenu?.offsetHeight ?? 280;
      setSubmenuPosition(clampedMainMenuSubmenuPosition(submenuAnchor, width, height));
    }

    updateSubmenuPosition();
    window.addEventListener("resize", updateSubmenuPosition);
    return () => window.removeEventListener("resize", updateSubmenuPosition);
  }, [activeSubmenu, submenuAnchor]);

  useEffect(() => {
    const openAbout = () => setAboutOpen(true);
    window.addEventListener(CURVA_OPEN_ABOUT_EVENT, openAbout);
    return () => window.removeEventListener(CURVA_OPEN_ABOUT_EVENT, openAbout);
  }, []);

  return (
    <>
    <header className="flex h-12 shrink-0 items-center border-b border-white/10 bg-[#1f2329] px-3 text-[12px] text-slate-200">
      <nav ref={menuRef} className="relative flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon-sm"
          title="Main menu"
          aria-label="Main menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(open => !open)}
          className="text-slate-300"
        >
          <Menu className="size-4" />
        </Button>
        {menuOpen ? (
          <div className="absolute left-0 top-9 z-20 min-w-44 rounded-md border border-white/10 bg-[#242a31] p-1 shadow-xl shadow-black/30">
            <div
              onMouseEnter={() => {
                setActiveSubmenu(undefined);
                setSubmenuAnchor(undefined);
              }}
            >
            <div className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">File</div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.requestNewDocument();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              <FilePlus2 className="size-4" />
              New
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.requestCurvaImport();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              <Upload className="size-4" />
              {isNativeShell() ? "Open…" : "Import .curva"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.requestComponentImport();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              <Upload className="size-4" />
              Import components
            </Button>
            <MainMenuSubmenuButton
              label="Scripts"
              active={activeSubmenu === "scripts"}
              onOpen={element => {
                setActiveSubmenu("scripts");
                setSubmenuAnchor(element.getBoundingClientRect());
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.exportCurvaFile();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              {isNativeShell() ? <Save className="size-4" /> : <Download className="size-4" />}
              {isNativeShell() ? "Save" : "Export .curva"}
            </Button>
            {isNativeShell() ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  actions.exportCurvaFileAs();
                  setMenuOpen(false);
                }}
                className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
              >
                <Save className="size-4" />
                Save As…
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.exportSelectedFramesPdf();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              <FileText className="size-4" />
              Export selected frames as PDF
            </Button>
            </div>
            <div className="my-1 h-px bg-white/10" />
            <MainMenuSubmenuButton
              label="View"
              active={activeSubmenu === "view"}
              onOpen={element => {
                setActiveSubmenu("view");
                setSubmenuAnchor(element.getBoundingClientRect());
              }}
            />
            <MainMenuSubmenuButton
              label="Development"
              active={activeSubmenu === "development"}
              onOpen={element => {
                setActiveSubmenu("development");
                setSubmenuAnchor(element.getBoundingClientRect());
              }}
            />
            <div className="my-1 h-px bg-white/10" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setMenuOpen(false);
                setAboutOpen(true);
              }}
              onMouseEnter={() => {
                setActiveSubmenu(undefined);
                setSubmenuAnchor(undefined);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              <Info className="size-4" />
              About
            </Button>
          </div>
        ) : null}
        {menuOpen && activeSubmenu === "scripts" ? (
          <div
            ref={submenuRef}
            className="fixed z-30 w-56 rounded-md border border-white/10 bg-[#242a31] p-1 shadow-xl shadow-black/30"
            style={{ left: submenuPosition.left, top: submenuPosition.top }}
            role="menu"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.requestScriptImport();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              Import scripts
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.requestScriptImportAndReplace();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              Import and replace scripts
            </Button>
            <div className="my-1 h-px bg-white/10" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.requestAddEditorScripts();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              Add editor scripts
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.requestReplaceEditorScripts();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              Replace editor scripts
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.clearEditorScripts();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              Clear editor scripts
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.openEditorScriptsDocument();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              Open editor scripts document
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.downloadEditorScriptsDocument();
                setMenuOpen(false);
              }}
              className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              Download editor scripts document
            </Button>
          </div>
        ) : null}
        {menuOpen && activeSubmenu === "view" ? (
          <div
            ref={submenuRef}
            className="fixed z-30 w-56 max-h-[min(28rem,calc(100vh-16px))] overflow-y-auto rounded-md border border-white/10 bg-[#242a31] p-1 shadow-xl shadow-black/30"
            style={{ left: submenuPosition.left, top: submenuPosition.top }}
            role="menu"
          >
            <MenuToggle
              label="Show Pixel Grid"
              checked={canvasDebug.showPixelGrid}
              onClick={() => uiStore.patchCanvasDebug({ showPixelGrid: !canvasDebug.showPixelGrid })}
            />
            <MenuToggle
              label="Show Rulers"
              checked={canvasDebug.showRulers}
              onClick={() => uiStore.setShowRulers(!canvasDebug.showRulers)}
            />
            <MenuToggle
              label="Show Animation Preview"
              checked={animation.previewWindowOpen}
              shortcut="Cmd+Shift+P"
              onClick={() => uiStore.setAnimationPreviewWindowOpen(!animation.previewWindowOpen)}
            />
            <MenuToggle
              label="Allow Scripts"
              checked={allowScripts}
              onClick={() => uiStore.setAllowScripts(!allowScripts)}
            />
            <MenuToggle
              label="Show Scripts Debug"
              checked={scriptsDebugWindowOpen}
              onClick={() => uiStore.setScriptsDebugWindowOpen(!scriptsDebugWindowOpen)}
            />
            <div className="my-1 h-px bg-white/10" />
            <MenuToggle
              label="Snap to Pixel Grid"
              checked={canvasSnap.snapToPixelGrid}
              onClick={() => uiStore.patchCanvasSnap({ snapToPixelGrid: !canvasSnap.snapToPixelGrid })}
            />
            <MenuToggle
              label="Snap to Objects"
              checked={canvasSnap.snapToObjects}
              onClick={() => uiStore.patchCanvasSnap({ snapToObjects: !canvasSnap.snapToObjects })}
            />
            <MenuToggle
              label="Snap to Guides"
              checked={canvasSnap.snapToGuides}
              onClick={() => uiStore.setSnapToGuides(!canvasSnap.snapToGuides)}
            />
            <MenuToggle
              label="Snap to Geometry"
              checked={canvasSnap.snapToGeometry}
              onClick={() => uiStore.patchCanvasSnap({ snapToGeometry: !canvasSnap.snapToGeometry })}
            />
            <div className="my-1 h-px bg-white/10" />
            <MenuToggle
              label="Assisted Pencil Tracing"
              checked={canvasSnap.assistedPencilTracing}
              onClick={() => uiStore.patchCanvasSnap({ assistedPencilTracing: !canvasSnap.assistedPencilTracing })}
            />
          </div>
        ) : null}
        {menuOpen && activeSubmenu === "development" ? (
          <div
            ref={submenuRef}
            className="fixed z-30 w-56 rounded-md border border-white/10 bg-[#242a31] p-1 shadow-xl shadow-black/30"
            style={{ left: submenuPosition.left, top: submenuPosition.top }}
            role="menu"
          >
            <MenuToggle
              label="Show Render Debug"
              checked={canvasDebug.showRenderDebug}
              shortcut="~"
              onClick={() => uiStore.patchCanvasDebug({ showRenderDebug: !canvasDebug.showRenderDebug })}
            />
            <MenuToggle
              label="Render Triangles"
              checked={canvasDebug.renderTriangles}
              onClick={() => uiStore.patchCanvasDebug({ renderTriangles: !canvasDebug.renderTriangles })}
            />
            <MenuToggle
              label="Tile Gaps"
              checked={canvasDebug.showTileGaps}
              onClick={() => uiStore.patchCanvasDebug({ showTileGaps: !canvasDebug.showTileGaps })}
            />
            <MenuToggle
              label="Wasm Tessellation"
              checked={canvasDebug.useWasmTessellation}
              onClick={() => uiStore.patchCanvasDebug({ useWasmTessellation: !canvasDebug.useWasmTessellation })}
            />
            <MenuToggle
              label="Connect to MCP Host"
              checked={mcpBridge.enabled}
              live={mcpBridge.status === "connected"}
              onClick={() => toggleMcpHostConnect()}
            />
          </div>
        ) : null}
      </nav>
      <div className="mx-2 h-5 w-px bg-white/10" />
      <Button
        variant="ghost"
        size="sm"
        title="Toggle animation mode (Cmd+Shift+A)"
        aria-label="Toggle animation mode"
        onClick={actions.toggleAnimationMode}
        className={`gap-1.5 px-2 text-[12px] ${animation.panelOpen ? "text-sky-400" : "text-slate-400 hover:text-slate-200"}`}
      >
        <Workflow className="size-3.5" />
        Anima
      </Button>

      <div className="flex flex-1 items-center justify-center">
        <ToolCluster actions={actions} toolPanel={toolPanel} />
      </div>

      <div className="flex min-w-[260px] items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="icon-sm"
          title="Undo (Cmd+Z)"
          aria-label="Undo"
          disabled={!canUndo}
          onClick={actions.undo}
          className="text-slate-300"
        >
          <Undo2 className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          title="Redo (Cmd+Shift+Z)"
          aria-label="Redo"
          disabled={!canRedo}
          onClick={actions.redo}
          className="text-slate-300"
        >
          <Redo2 className="size-4" />
        </Button>
        <div className="mx-2 h-5 w-px bg-white/10" />
        <DocumentNameField name={documentName} onCommit={actions.renameDocument} />
        <Button
          variant="ghost"
          size="icon-sm"
          title="Save"
          aria-label="Save"
          className="text-slate-300"
          onClick={actions.exportCurvaFile}
        >
          <Save className="size-4" />
        </Button>
      </div>
    </header>
    {aboutOpen ? <AboutDialog onClose={() => setAboutOpen(false)} /> : null}
    </>
  );
}

function DocumentNameField({ name, onCommit }: { name: string; onCommit: (name: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  useEffect(() => {
    if (!editing) {
      setDraft(name);
    }
  }, [editing, name]);

  if (editing) {
    return (
      <input
        className="max-w-40 rounded-sm border border-sky-400/70 bg-[#0f1318] px-2 py-1 text-[12px] font-medium text-slate-50 outline-none"
        value={draft}
        aria-label="Document name"
        autoFocus
        onFocus={event => event.currentTarget.select()}
        onChange={event => setDraft(event.target.value)}
        onBlur={() => {
          setEditing(false);
          onCommit(draft);
        }}
        onKeyDown={event => {
          if (event.key === "Enter") {
            event.preventDefault();
            onCommit(draft);
            setEditing(false);
          } else if (event.key === "Escape") {
            event.preventDefault();
            setDraft(name);
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      title="Rename document"
      aria-label={`Document name: ${name}`}
      onClick={() => setEditing(true)}
      className="max-w-40 truncate rounded-sm px-2 py-1 text-left text-[12px] font-medium text-slate-100 transition-colors hover:bg-white/[0.06]"
    >
      {name}
    </button>
  );
}

export function FloatingToolPanel({ actions }: { actions: TopBarActions }) {
  const toolPanel = useToolPanelState();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const positionRef = useRef<PanelPosition | undefined>(undefined);
  const dragRef = useRef<
    | {
        pointerId: number;
        startClientX: number;
        startClientY: number;
        startX: number;
        startY: number;
      }
    | undefined
  >(undefined);
  const [position, setPosition] = useState<PanelPosition | undefined>(undefined);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  function commitPosition(nextPosition: PanelPosition): void {
    positionRef.current = nextPosition;
    setPosition(nextPosition);
  }

  useLayoutEffect(() => {
    placeFloatingToolPanel(panelRef.current, positionRef.current ?? readFloatingToolPanelPosition(), commitPosition);
  }, [toolPanel.mode]);

  useEffect(() => {
    const handleResize = () => {
      placeFloatingToolPanel(panelRef.current, positionRef.current, commitPosition, true);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function handleDragPointerDown(event: ReactPointerEvent<HTMLButtonElement>): void {
    const panel = panelRef.current;
    const currentPosition = positionRef.current;
    if (!panel || !currentPosition) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: currentPosition.x,
      startY: currentPosition.y,
    };
    setDragging(true);
  }

  function handleDragPointerMove(event: ReactPointerEvent<HTMLButtonElement>): void {
    const panel = panelRef.current;
    const drag = dragRef.current;
    if (!panel || !drag || drag.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    const nextPosition = clampFloatingToolPanelPosition(
      {
        x: drag.startX + event.clientX - drag.startClientX,
        y: drag.startY + event.clientY - drag.startClientY,
      },
      panel.offsetWidth,
      panel.offsetHeight,
    );
    commitPosition(nextPosition);
  }

  function finishDrag(event: ReactPointerEvent<HTMLButtonElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragRef.current = undefined;
    setDragging(false);
    if (positionRef.current) {
      writeFloatingToolPanelPosition(positionRef.current);
    }
  }

  return (
    <div
      ref={panelRef}
      className="pointer-events-auto absolute flex h-10 max-w-[calc(100vw-1rem)] items-center overflow-visible rounded-md border border-white/10 bg-[#1f2329]/95 pr-2 text-[12px] text-slate-200 shadow-xl shadow-black/30 backdrop-blur"
      style={position ? { left: position.x, top: position.y } : { left: 0, top: 0, visibility: "hidden" }}
    >
      <button
        type="button"
        title="Move toolbar"
        aria-label="Move toolbar"
        className={`flex h-full w-5 cursor-grab touch-none items-center justify-center rounded-l-md border-r border-white/10 text-slate-500 hover:bg-white/[0.06] hover:text-slate-300 ${
          dragging ? "cursor-grabbing bg-white/[0.08] text-slate-300" : ""
        }`}
        onPointerDown={handleDragPointerDown}
        onPointerMove={handleDragPointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
      >
        <GripVertical className="size-3.5" />
      </button>
      <div className="pl-2">
        <ToolCluster actions={actions} toolPanel={toolPanel} />
      </div>
    </div>
  );
}

function ToolCluster({
  actions,
  toolPanel,
}: {
  actions: TopBarActions;
  toolPanel: ReturnType<typeof useToolPanelState>;
}) {
  const {
    activeTool,
    mode,
    selectedShapeKind,
    selectedVertexCount,
    selectedEdgeCount,
    hasSelectedHandle,
    hasSelection,
    selectionColorSampleActive,
  } = toolPanel;
  const [shapeMenuOpen, setShapeMenuOpen] = useState(false);
  const [booleanMenuOpen, setBooleanMenuOpen] = useState(false);
  const shapeMenuRef = useRef<HTMLDivElement | null>(null);
  const booleanMenuRef = useRef<HTMLDivElement | null>(null);
  const selectedShape = shapeTools.find(shape => shape.kind === selectedShapeKind) ?? shapeTools[0]!;
  const SelectedShapeIcon = selectedShape.icon;
  const KnifeIcon = selectedVertexCount === 1 ? ScissorsLineDashed : PocketKnife;

  useEffect(() => {
    if (!shapeMenuOpen && !booleanMenuOpen) {
      return;
    }

    const closeMenus = () => {
      setShapeMenuOpen(false);
      setBooleanMenuOpen(false);
    };
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Node ? event.target : undefined;
      if (target && (shapeMenuRef.current?.contains(target) || booleanMenuRef.current?.contains(target))) {
        return;
      }

      closeMenus();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenus();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [booleanMenuOpen, shapeMenuOpen]);

  return (
    <div className="flex items-center justify-center gap-1">
      {tools.map(({ id, label, shortcut, icon: Icon }) => {
        const active = activeTool === id;
        return (
          <Button
            key={label}
            variant={active ? "secondary" : "ghost"}
            size="icon-sm"
            title={tooltipWithShortcut(label, shortcut)}
            aria-label={label}
            onClick={() => actions.changeTool(id)}
            className={active ? "bg-sky-500/20 text-sky-200 hover:bg-sky-500/25" : "text-slate-300"}
          >
            <Icon className="size-4" />
          </Button>
        );
      })}

      <div ref={shapeMenuRef} className="relative flex items-center">
        <Button
          variant={activeTool === "shape" ? "secondary" : "ghost"}
          size="icon-sm"
          title={tooltipWithShortcut(selectedShape.label, selectedShape.shortcut)}
          aria-label={selectedShape.label}
          onClick={() => actions.changeTool("shape")}
          className={
            activeTool === "shape"
              ? "rounded-r-none bg-sky-500/20 text-sky-200 hover:bg-sky-500/25"
              : "rounded-r-none text-slate-300"
          }
        >
          <SelectedShapeIcon className="size-4" />
        </Button>
        <Button
          variant={activeTool === "shape" ? "secondary" : "ghost"}
          size="icon-sm"
          title="Shape tools"
          aria-label="Shape tools"
          aria-expanded={shapeMenuOpen}
          onClick={() => setShapeMenuOpen(open => !open)}
          className={
            activeTool === "shape"
              ? "w-5 rounded-l-none bg-sky-500/20 px-0 text-sky-200 hover:bg-sky-500/25"
              : "w-5 rounded-l-none px-0 text-slate-300"
          }
        >
          <ChevronDown className="size-3" />
        </Button>
        {shapeMenuOpen ? (
          <div className="absolute left-0 top-9 z-20 min-w-40 rounded-md border border-white/10 bg-[#242a31] p-1 shadow-xl shadow-black/30">
            {shapeTools.map(({ kind, label, shortcut, icon: Icon }) => (
              <Button
                key={kind}
                variant="ghost"
                size="sm"
                onClick={() => {
                  actions.changeShapeKind(kind);
                  setShapeMenuOpen(false);
                }}
                className="h-8 w-full justify-between gap-3 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Icon className="size-4 shrink-0" />
                  <span>{label}</span>
                </span>
                {shortcut ? <span className="text-[11px] text-slate-500">{shortcut}</span> : null}
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      <div ref={booleanMenuRef} className="relative flex items-center">
        <Button
          variant="ghost"
          size="icon-sm"
          title="Boolean operations"
          aria-label="Boolean operations"
          aria-expanded={booleanMenuOpen}
          onClick={() => setBooleanMenuOpen(open => !open)}
          className="rounded-r-none text-slate-300"
        >
          <SquaresUnite className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          title="Boolean operations menu"
          aria-label="Boolean operations menu"
          aria-expanded={booleanMenuOpen}
          onClick={() => setBooleanMenuOpen(open => !open)}
          className="w-5 rounded-l-none px-0 text-slate-300"
        >
          <ChevronDown className="size-3" />
        </Button>
        {booleanMenuOpen ? (
          <div className="absolute left-0 top-9 z-20 min-w-52 rounded-md border border-white/10 bg-[#242a31] p-1 shadow-xl shadow-black/30">
            {booleanTools.map(({ operation, label, shortcut, icon: Icon }) => (
              <Button
                key={operation}
                variant="ghost"
                size="sm"
                onClick={() => {
                  actions.applyBooleanOperation(operation);
                  setBooleanMenuOpen(false);
                }}
                className="h-8 w-full justify-between gap-3 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Icon className="size-4 shrink-0" />
                  <span>{label}</span>
                </span>
                <span className="text-[11px] text-slate-500">{shortcut}</span>
              </Button>
            ))}
            <div className="my-1 h-px bg-white/10" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.flattenSelectedNodes();
                setBooleanMenuOpen(false);
              }}
              className="h-8 w-full justify-between gap-3 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Scissors className="size-4 shrink-0" />
                <span>Flatten</span>
              </span>
              <span className="text-[11px] text-slate-500">Cmd+E</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                actions.flattenSelectedNodes({ planarize: true });
                setBooleanMenuOpen(false);
              }}
              className="h-8 w-full justify-between gap-3 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Scissors className="size-4 shrink-0" />
                <span>Planarize</span>
              </span>
              <span className="text-[11px] text-slate-500">Shift+Cmd+E</span>
            </Button>
          </div>
        ) : null}
      </div>

      {((mode === "vectorEdit") || hasSelection && mode !== "textEdit") && <div className="mx-1 h-5 w-px bg-white/10" />}

      {mode === "vectorEdit" ? (
        <>
          <Button
            variant={activeTool === "fill" ? "secondary" : "ghost"}
            size="icon-sm"
            title="Fill (G)"
            aria-label="Fill"
            onClick={() => actions.changeTool("fill")}
            className={activeTool === "fill" ? "bg-sky-500/20 text-sky-200 hover:bg-sky-500/25" : "text-slate-300"}
          >
            <PaintBucket className="size-4" />
          </Button>
          <Button
            variant={activeTool === "knife" ? "secondary" : "ghost"}
            size="icon-sm"
            title={selectedVertexCount === 1 ? "Knife: cut around point (K)" : "Knife: cut edges (K)"}
            aria-label={selectedVertexCount === 1 ? "Knife cut around point" : "Knife cut edges"}
            onClick={() => actions.changeTool("knife")}
            className={activeTool === "knife" ? "bg-sky-500/20 text-sky-200 hover:bg-sky-500/25" : "text-slate-300"}
          >
            <KnifeIcon className="size-4" />
          </Button>
          <Button
            variant={activeTool === "shapeBuilder" ? "secondary" : "ghost"}
            size="icon-sm"
            title="Shape Builder (M)"
            aria-label="Shape Builder"
            onClick={() => actions.changeTool("shapeBuilder")}
            className={activeTool === "shapeBuilder" ? "bg-sky-500/20 text-sky-200 hover:bg-sky-500/25" : "text-slate-300"}
          >
            <Shapes className="size-4" />
          </Button>
        </>
      ) : null}

      {hasSelection && mode !== "textEdit" ? (
        <Button
          variant={selectionColorSampleActive ? "secondary" : "ghost"}
          size="icon-sm"
          title="Sample color (I, Shift: stroke)"
          aria-label="Sample color"
          aria-pressed={selectionColorSampleActive}
          onClick={actions.startSelectionColorSample}
          className={selectionColorSampleActive ? "bg-sky-500/20 text-sky-200 hover:bg-sky-500/25" : "text-slate-300"}
        >
          <Pipette className="size-4" />
        </Button>
      ) : null}

      {mode === "vectorEdit" ? (
        <>
          <Button
            variant={activeTool === "sculpt" ? "secondary" : "ghost"}
            size="icon-sm"
            title="Sculpt brush (B)"
            aria-label="Sculpt brush"
            onClick={() => actions.changeTool("sculpt")}
            className={activeTool === "sculpt" ? "bg-sky-500/20 text-sky-200 hover:bg-sky-500/25" : "text-slate-300"}
          >
            <Brush className="size-4" />
          </Button>
          {activeTool === "sculpt" ? (
            <>
              <ToolbarSculptField
                label="Size"
                value={toolPanel.sculptBrush.size}
                min={2}
                max={1000}
                suffix="px"
                onChange={size => uiStore.patchSculptBrush({ size })}
              />
              <ToolbarSculptField
                label="Strength"
                value={toolPanel.sculptBrush.strength}
                min={1}
                max={100}
                suffix="%"
                onChange={strength => uiStore.patchSculptBrush({ strength })}
              />
            </>
          ) : null}
          {selectedEdgeCount > 0 ? (
            <>
              <div className="mx-1 h-5 w-px bg-white/10" />
              <Button
                variant={activeTool === "extrude" ? "secondary" : "ghost"}
                size="icon-sm"
                title="Extrude edges (E)"
                aria-label="Extrude edges"
                onClick={() => actions.changeTool("extrude")}
                className={activeTool === "extrude" ? "bg-sky-500/20 text-sky-200 hover:bg-sky-500/25" : "text-slate-300"}
              >
                <SquareArrowOutUpRight className="size-4" />
              </Button>
              <Button
                variant={activeTool === "subdivide" ? "secondary" : "ghost"}
                size="icon-sm"
                title="Subdivide edges"
                aria-label="Subdivide edges"
                onClick={() => actions.enterSubdivide()}
                className={activeTool === "subdivide" ? "bg-sky-500/20 text-sky-200 hover:bg-sky-500/25" : "text-slate-300"}
              >
                <Split className="size-4" />
              </Button>
              {activeTool === "subdivide" && toolPanel.subdivideSession ? (
                <>
                  <ToolbarSculptField
                    label="Segments"
                    value={toolPanel.subdivideSession.segments}
                    min={2}
                    max={64}
                    onChange={segments => actions.setSubdivideSegments(segments)}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    title="Commit subdivide"
                    aria-label="Commit subdivide"
                    onClick={() => actions.commitSubdivide()}
                    className="h-7 gap-1 bg-sky-500/20 px-2 text-[11px] text-sky-100 hover:bg-sky-500/25"
                  >
                    <Check className="size-3.5" />
                    Commit
                  </Button>
                </>
              ) : null}
            </>
          ) : null}
          {selectedVertexCount === 0 && selectedEdgeCount === 0 && !hasSelectedHandle ? (
            <Button
              variant="ghost"
              size="icon-sm"
              title="Split to regions"
              aria-label="Split to regions"
              onClick={() => actions.splitToRegions()}
              className="text-slate-300"
            >
              <Ungroup className="size-4" />
            </Button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function ToolbarSculptField({
  label,
  value,
  min,
  max,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex h-7 items-center gap-1 rounded bg-white/[0.04] px-1.5 text-[10px] text-slate-400">
      <NumericScrubLabel label={label} value={value} min={min} max={max} onChange={onChange} />
      <ScrubInput
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={event => {
          const next = Number(event.currentTarget.value);
          if (Number.isFinite(next)) onChange(next);
        }}
        className="h-5 w-10 border-0 bg-transparent p-0 text-right text-[11px]"
      />
      {suffix ? <span>{suffix}</span> : null}
    </label>
  );
}

function MenuToggle({
  label,
  checked,
  shortcut,
  live,
  onClick,
}: {
  label: string;
  checked: boolean;
  shortcut?: string;
  /** Accent status indicator (e.g. MCP host handshaken). */
  live?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="h-8 w-full justify-start gap-2 px-2 text-[12px] text-slate-300 hover:bg-white/[0.08]"
    >
      <span className="flex size-4 shrink-0 items-center justify-center">
        {checked ? <Check className="size-3.5" /> : null}
      </span>
      <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
        <span className="truncate">{label}</span>
        {live ? (
          <span
            className="size-1.5 shrink-0 rounded-full bg-sky-400 shadow-[0_0_6px_rgba(56,189,248,0.85)]"
            title="Connected"
            aria-label="Connected"
          />
        ) : null}
      </span>
      {shortcut ? <span className="text-[11px] text-slate-500">{shortcut}</span> : null}
    </Button>
  );
}

function MainMenuSubmenuButton({
  label,
  active,
  onOpen,
}: {
  label: string;
  active: boolean;
  onOpen: (element: HTMLElement) => void;
}) {
  return (
    <button
      type="button"
      className={`flex h-8 w-full items-center justify-between rounded px-2 text-left text-[12px] text-slate-300 ${
        active ? "bg-white/[0.08]" : "hover:bg-white/[0.08]"
      }`}
      onMouseEnter={event => onOpen(event.currentTarget)}
      onFocus={event => onOpen(event.currentTarget)}
      onClick={event => onOpen(event.currentTarget)}
      role="menuitem"
      aria-haspopup="menu"
      aria-expanded={active}
    >
      <span>{label}</span>
      <ChevronRight className="size-3.5 text-slate-500" aria-hidden="true" />
    </button>
  );
}

function clampedMainMenuSubmenuPosition(
  anchor: DOMRect,
  width: number,
  height: number,
): { left: number; top: number } {
  const margin = 8;
  const gap = 4;
  const right = anchor.right + gap;
  const left = anchor.left - width - gap;
  const preferredLeft = right + width <= window.innerWidth - margin ? right : left;
  return {
    left: Math.min(Math.max(margin, preferredLeft), Math.max(margin, window.innerWidth - width - margin)),
    top: Math.min(Math.max(margin, anchor.top), Math.max(margin, window.innerHeight - height - margin)),
  };
}

function placeFloatingToolPanel(
  panel: HTMLDivElement | null,
  preferredPosition: PanelPosition | undefined,
  setPosition: (position: PanelPosition) => void,
  persist = false,
): void {
  if (!panel) {
    return;
  }

  const fallbackPosition = {
    x: (window.innerWidth - panel.offsetWidth) / 2,
    y: 12,
  };
  const nextPosition = clampFloatingToolPanelPosition(
    preferredPosition ?? fallbackPosition,
    panel.offsetWidth,
    panel.offsetHeight,
  );
  setPosition(nextPosition);
  if (persist) {
    writeFloatingToolPanelPosition(nextPosition);
  }
}

function clampFloatingToolPanelPosition(position: PanelPosition, width: number, height: number): PanelPosition {
  const maxX = Math.max(floatingToolPanelViewportMargin, window.innerWidth - width - floatingToolPanelViewportMargin);
  const maxY = Math.max(floatingToolPanelViewportMargin, window.innerHeight - height - floatingToolPanelViewportMargin);
  return {
    x: clamp(position.x, floatingToolPanelViewportMargin, maxX),
    y: clamp(position.y, floatingToolPanelViewportMargin, maxY),
  };
}

function readFloatingToolPanelPosition(): PanelPosition | undefined {
  try {
    const rawValue = window.localStorage.getItem(floatingToolPanelPositionStorageKey);
    if (!rawValue) {
      return undefined;
    }

    const parsed = JSON.parse(rawValue) as Partial<PanelPosition>;
    return typeof parsed.x === "number" && Number.isFinite(parsed.x) && typeof parsed.y === "number" && Number.isFinite(parsed.y)
      ? { x: parsed.x, y: parsed.y }
      : undefined;
  } catch {
    return undefined;
  }
}

function writeFloatingToolPanelPosition(position: PanelPosition): void {
  try {
    window.localStorage.setItem(floatingToolPanelPositionStorageKey, JSON.stringify(position));
  } catch {
    // Ignore storage failures; dragging should still work for this session.
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
